import { isRequestDrawerEnabledForTenant, RequestDrawers as Drawers } from '@pelicargo/tenants';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { AirlineBookingDrawer } from './Drawers/AirlineBookingDrawer';
import { BookingCancelDrawer } from './Drawers/BookingCancelDrawer';
import { BookingChangeDrawer } from './Drawers/BookingChangeDrawer';
import { BookingConfirmDrawer } from './Drawers/BookingConfirmDrawer';
import { ContactFormDrawer } from './Drawers/ContactFormDrawer';
import { GenericEmailDrawer } from './Drawers/GenericEmailDrawer';
import { NewRateDrawer } from './Drawers/NewRateDrawer';
import { ProviderQuotingDrawer } from './Drawers/ProviderQuotingDrawer';
import { QuoteUpdateDrawer } from './Drawers/QuoteUpdateDrawer';
import { RequestClarificationsDrawer } from './Drawers/RequestClarificationsDrawer';
import { RequestScheduleDrawer } from './Drawers/RequestScheduleDrawer';
import { StatusUpdateEmailDrawer } from './Drawers/StatusUpdateEmailDrawer';

type FirstParam<T extends (...a: any) => any> = Parameters<T> extends [any] ? Parameters<T>[0] : any;

type DrawerFunctions = {
  ProviderQuoting: FirstParam<typeof ProviderQuotingDrawer>;
  RequestClarifications: FirstParam<typeof RequestClarificationsDrawer>;
  QuoteUpdate: FirstParam<typeof QuoteUpdateDrawer>;
  AirlineBooking: FirstParam<typeof AirlineBookingDrawer>;
  BookingConfirmation: FirstParam<typeof BookingConfirmDrawer>;
  BookingChange: FirstParam<typeof BookingChangeDrawer>;
  BookingCancel: FirstParam<typeof BookingCancelDrawer>;
  NewRate: FirstParam<typeof NewRateDrawer>;
  Contact: FirstParam<typeof ContactFormDrawer>;
  RequestSchedule: FirstParam<typeof RequestScheduleDrawer>;
  StatusUpdate: FirstParam<typeof StatusUpdateEmailDrawer>;
  GenericEmailToFF: FirstParam<typeof GenericEmailDrawer>;
};

type RequestDrawersContextValues = {
  openModal: <T extends Drawers>(modalName: T, data?: DrawerFunctions[T]) => void;
  closeModal: (modalName: Drawers) => void;
  getIsModalOpen: (modalName: Drawers) => boolean;
  getModalData: (modalName: Drawers) => any;
};

export const RequestDrawersContext = createContext({} as RequestDrawersContextValues);
export const useRequestDrawersContext = () => useContext(RequestDrawersContext);

type DrawerState = {
  data: {
    [key in Drawers]: DrawerFunctions[key];
  };
} & {
  [key in Drawers]: boolean;
};

type Props = {
  children: ReactNode;
};

export const RequestDrawers = ({ children }: Props) => {
  const [drawerState, setDrawerState] = useState<DrawerState>({
    ProviderQuoting: false,
    RequestClarifications: false,
    QuoteUpdate: false,
    AirlineBooking: false,
    BookingConfirmation: false,
    BookingChange: false,
    BookingCancel: false,
    NewRate: false,
    Contact: false,
    RequestSchedule: false,
    GenericEmailToFF: false,
    StatusUpdate: false,
    data: {
      ProviderQuoting: {},
      QuoteUpdate: {},
      RequestClarifications: {},
      AirlineBooking: {},
      BookingConfirmation: {},
      BookingChange: {},
      BookingCancel: {},
      NewRate: {},
      Contact: {},
      RequestSchedule: {},
      GenericEmailToFF: {},
      StatusUpdate: {},
    },
  });

  const openModal = (modalName: Drawers, data?: any) => {
    if (!isRequestDrawerEnabledForTenant(modalName)) return;

    setDrawerState((prev) => {
      return {
        ...prev,
        [modalName]: true,
        data: {
          ...prev.data,
          [modalName]: data,
        },
      };
    });
  };

  const closeModal = (modalName: Drawers) => {
    setDrawerState((prev) => {
      return {
        ...prev,
        [modalName]: false,
        data: {
          ...prev.data,
          [modalName]: {},
        },
      };
    });
  };

  const getIsModalOpen = useCallback((modalName: Drawers) => drawerState[modalName], [drawerState]);
  const getModalData = useCallback((modalName: Drawers) => drawerState.data[modalName], [drawerState.data]);

  return (
    <RequestDrawersContext.Provider value={{ openModal, closeModal, getIsModalOpen, getModalData }}>
      {children}
    </RequestDrawersContext.Provider>
  );
};
